import { gql, useMutation } from '@apollo/client'
import cx from 'classnames'
import { Link } from 'gatsby'
import rollbar from 'helpers/rollbar'
import { useToast } from './Toast'
import { MatchResult, MatchResultVariables } from './__generated__/MatchResult'

export const matchResultMutation = gql`
  mutation MatchResult(
    $id: ID!
    $kitId: ID!
    $versionId: ID
    $regCorrections: RegistrationCorrectionsInput
    $labEventCorrections: LabEventCorrectionsInput
  ) {
    matchResult(
      resultId: $id
      kitId: $kitId
      versionId: $versionId
      regCorrections: $regCorrections
      labEventCorrections: $labEventCorrections
    ) {
      result {
        id
        status
      }
    }
  }
`

interface ConfirmMatchButtonProps {
  resultId: string
  kitId?: string
  versionId?: string
  promptForKitId?: boolean
  disabled?: boolean
  children: React.ReactNode
  regCorrections?: {
    firstName?: string
    lastName?: string
    dob?: string
  }
}

const ConfirmMatchButton = ({
  resultId,
  kitId,
  versionId,
  promptForKitId,
  disabled = false,
  children,
  regCorrections,
}: ConfirmMatchButtonProps) => {
  const { addToast } = useToast()

  const [matchResult, { loading }] = useMutation<MatchResult, MatchResultVariables>(matchResultMutation)

  const getKitId = () => {
    if (kitId) return kitId

    if (promptForKitId) {
      return window.prompt(
        'Please enter the kit ID to match this result to. There is no "undo", so be careful. The kit MUST be registered or this will fail.'
      )
    }

    throw new Error('No kit ID supplied and prompt was not set')
  }

  const onMatch = async () => {
    const actualKitId = getKitId()

    if (!actualKitId) {
      return
    }

    try {
      await matchResult({
        variables: {
          id: resultId,
          kitId: actualKitId,
          versionId,
          regCorrections,
        },
      })
      addToast({
        messageType: 'success',
        title: 'Matched',
        content: (
          <>
            Matched result to kit <Link to={`/dashboard/kits?kitId=${actualKitId}`}>{actualKitId}</Link>.
          </>
        ),
        timeout: 3000,
      })
    } catch (err) {
      rollbar.error('Error matching result', err)
      addToast({
        messageType: 'error',
        title: 'Error',
        content: `Failed to match result: ${err.message}`,
      })
    }
  }

  return (
    <button
      className={cx('button is-small is-primary is-outlined is-fullwidth', {
        'is-loading': loading,
      })}
      disabled={loading || disabled}
      onClick={onMatch}
    >
      {children}
    </button>
  )
}

export default ConfirmMatchButton
